import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { config as appConfig } from '@/config';
import type { IStyleProfile, StyleProfileBrowseCard } from '@/types';
import { isRecord } from '@/utils';
import {
  applyValidatedExplicitOrganizationHeaders,
  getValidatedStoredOrganizationHeaderValues,
  hasExplicitOrganizationHeaders,
} from './client';
import { handle401WithSilentRefresh } from './authRefresh';
import { getCurrentAuthTokens } from './auth-session';
import {
  recordAgentServiceFailure,
  recordAgentServiceSuccess,
} from './agentServiceAvailability';
import type {
  PlanQuestion,
  PlanConversationResponse,
  PlanCompilationResponse,
  PlanResearchContext,
  ProposedAgent,
  ProposedKnowledgeBase,
  ProposedPageDesign,
  ProposedSchema,
  ProposedSkill,
  ProposedWorkflow,
  Roadmap,
  AuditResult,
  PhasePrompt,
  SavedRoadmapSummary,
} from '@/types/plan';
import type { ColorScheme, ColorSchemeWriteTarget } from '@/types/color-scheme';
import type { HAAutomationConfig } from '@/types/haAutomation';
import type { AiBindingContextSummary, AiBindingInstructionSurface } from '@/services/ai/BindingContextSummary';
import type { ComponentDataWriteOperation } from '@/types/componentDataBinding';

// Create agent service axios instance
export const createAgentClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: `${appConfig.api.agentServiceUrl}/api/v1`,
    timeout: 120000, // 120s timeout for LLM calls (visual editor agent can take 60-90s)
    headers: { 'Content-Type': 'application/json' },
  });

  // Add auth token from main app's storage
  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const tokens = getCurrentAuthTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }

    const hasPerRequestOrgOverride =
      hasExplicitOrganizationHeaders(config.headers);

    if (hasPerRequestOrgOverride) {
      await applyValidatedExplicitOrganizationHeaders(config.headers);
    } else {
      const organizationHeaders = await getValidatedStoredOrganizationHeaderValues();
      for (const [headerName, headerValue] of Object.entries(organizationHeaders)) {
        config.headers[headerName] = headerValue;
      }
    }

    return config;
  });

  // Handle 401 responses via the shared silent-refresh-then-modal handler.
  // This matches client.ts / data-service-client.ts so a revoked-jti 401
  // recovers identically across services instead of leaving one service
  // (e.g. agent-service) stuck while another silently works.
  client.interceptors.response.use(
    (response) => {
      recordAgentServiceSuccess();
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === undefined || status >= 500) {
          recordAgentServiceFailure(error.message);
        }
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        const hadToken = getCurrentAuthTokens();
        if (hadToken?.accessToken) {
          originalRequest._retry = true;
          try {
            return await handle401WithSilentRefresh(error, client);
          } catch (handlerError) {
            return Promise.reject(handlerError);
          }
        }
      }

      // Handle 429 rate limiting with exponential backoff
      if (error.response?.status === 429 && !originalRequest._rateLimitRetry) {
        const retryCount = originalRequest._rateLimitRetryCount || 0;
        if (retryCount < 3) {
          originalRequest._rateLimitRetry = true;
          originalRequest._rateLimitRetryCount = retryCount + 1;
          const retryAfter = error.response.headers['retry-after'];
          const delay = retryAfter
            ? parseInt(retryAfter, 10) * 1000
            : Math.min(1000 * Math.pow(2, retryCount), 8000);

          console.warn(`[AgentAPI] Rate limited, retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);

          return new Promise((resolve) => {
            setTimeout(() => {
              originalRequest._rateLimitRetry = false;
              resolve(client(originalRequest));
            }, delay);
          });
        }
        console.warn('[AgentAPI] Rate limit retries exhausted');
      }

      return Promise.reject(error);
    }
  );

  return client;
};

const agentClient = createAgentClient();

// --- Types ---

export interface OrchestratorConfig {
  _id: string;
  name: string;
  status: 'active' | 'inactive' | 'maintenance';
  capabilities: string[];
  triggers: Array<{ type: string; config: Record<string, unknown> }>;
  system_prompt: string;
  knowledge_entries?: string[];
  resource_preferences: {
    default_model_tier: number;
    max_expert_agents: number;
    budget_priority: 'low' | 'medium' | 'high' | 'critical';
  };
  mqtt_topics: { command: string; status: string; discovery: string };
  organization_id: string;
  created_by: string;
  voice_config?: {
    blend: Array<{ voice_name: string; weight: number }>;
    updatedAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AgentEvent {
  _id: string;
  event_id: string;
  description: string;
  category: string;
  scope: string;
  temporal_shape: string;
  started_at: string;
  ended_at?: string;
  ranking: { impact: number; urgency: number; confidence: number; attention: number };
  composite_score: number;
  domains: string[];
  organization_id: string;
  community_id?: string;
  tier: string;
  parent_event_id?: string;
  causal_links: string[];
  created_by?: string;
  createdAt: string;
}

export interface AgentMemory {
  _id: string;
  memory_id: string;
  text: string;
  weight: number;
  type: 'hypothesis' | 'fact' | 'directive' | 'correction';
  aspects: string[];
  event_anchor?: string;
  organization_id: string;
  community_id?: string;
  created_by?: string;
  created_at: string;
  last_used: string;
  use_count: number;
  feedback_score: number;
  origin?: 'agent' | 'human' | 'web';
  archived?: boolean;
  category?: string;
  supersedes?: string;
  superseded_by?: string;
  parent_id?: string;
  confirmed_by?: string;
  confirmed_at?: string;
  valid_from?: string;
  valid_to?: string;
  stale_after?: string;
  sources?: Array<{ url: string; title: string; retrieved_at: string; authority: string }>;
}

export interface ResourceBudget {
  _id: string;
  organization_id: string;
  budget_type: 'daily' | 'monthly';
  total_budget_tokens: number;
  total_budget_cost_usd: number;
  allocated: Record<string, { tokens_allocated: number; cost_allocated: number; tokens_used: number; cost_used: number }>;
  period_start: string;
  period_end: string;
}

export interface SpendingSummary {
  totals: { totalCost: number; totalTokens: number; requestCount: number };
  daily: Array<{ date: string; cost: number; tokens: number; requests: number }>;
  byModel: Array<{ model: string; cost: number; tokens: number; requests: number }>;
}

export interface Community {
  _id: string;
  community_id: string;
  name: string;
  type: 'system' | 'human';
  organization_id: string;
  owner_user_id?: string;
  orchestrator_config_id?: string;
  status: string;
  privacy_level: 'public' | 'private';
  createdAt: string;
}

interface ApiRes<T> {
  success: boolean;
  data: T;
  total?: number;
}

interface MemoryFeedbackResponse {
  memoriesAdjusted: number;
  correctionMemoryId?: string;
  message?: string;
}

interface ResourceBudgetAllocationRequest {
  community_id: string;
  tokens_allocated: number;
  cost_allocated: number;
}

interface ResourceSummaryData {
  currentBudget?: ResourceBudget | null;
  totalAllocatedTokens?: number;
  totalAllocatedCostUsd?: number;
  totalUsedTokens?: number;
  totalUsedCostUsd?: number;
  remainingTokens?: number;
  remainingCostUsd?: number;
  [key: string]: unknown;
}

interface ResourceUsageLog {
  [key: string]: unknown;
}

interface ResourceUsageByTierEntry {
  tier?: number;
  requests?: number;
  tokens?: number;
  cost_usd?: number;
  [key: string]: unknown;
}

interface SkillWizardResponse {
  message: string;
  skillReady: boolean;
  questions?: PlanQuestion[];
  proposedSkill?: ProposedSkill;
  turnIndex: number;
  threadId?: string;
}

interface AgentWizardResponse {
  message: string;
  agentReady: boolean;
  questions?: PlanQuestion[];
  proposedAgent?: ProposedAgent;
  turnIndex: number;
  threadId?: string;
}

interface KnowledgeWizardResponse {
  message: string;
  knowledgeReady: boolean;
  questions?: PlanQuestion[];
  proposedKnowledgeBase?: ProposedKnowledgeBase;
  turnIndex: number;
  threadId?: string;
}

interface WorkflowWizardResponse {
  message: string;
  workflowReady: boolean;
  questions?: PlanQuestion[];
  proposedWorkflow?: ProposedWorkflow;
  turnIndex: number;
  threadId?: string;
}

interface DataWizardResponse {
  message: string;
  dataReady: boolean;
  questions?: PlanQuestion[];
  proposedSchema?: ProposedSchema;
  turnIndex: number;
  threadId?: string;
}

interface PageDesignWizardResponse {
  message: string;
  designReady: boolean;
  questions?: PlanQuestion[];
  proposedPageDesign?: ProposedPageDesign;
  turnIndex: number;
  threadId?: string;
}

interface LiaisonPhaseExecutionResponse {
  response?: string;
  metadata?: {
    schemas?: Array<Record<string, unknown>>;
    queryTemplates?: Array<Record<string, unknown>>;
    bindings?: Array<Record<string, unknown>>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface RegeneratePhaseResponse {
  phase: {
    phaseId: string;
    executionPrompt: string;
    domain: string;
    enrichmentTokens: string[];
    dependencies: string[];
  };
  _debug?: Record<string, unknown>;
}

interface UserAgentTaskResult {
  status: 'pending' | 'completed' | 'not_found' | 'no_agent';
  response?: string;
  error?: string;
  usage?: ChatResponse['usage'];
  warnings?: string[];
  agentName?: string;
}

// --- Registry API ---

export interface AssembledPrompt {
  system_prompt: string;
  knowledge_entries: string[];
  assembled: string;
}

export const registryApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<OrchestratorConfig[]>>('/registry', { params }).then(r => r.data),
  listAll: () =>
    agentClient.get<ApiRes<OrchestratorConfig[]>>('/registry', { params: { includeSystem: 'true' } }).then(r => r.data),
  get: (id: string) =>
    agentClient.get<ApiRes<OrchestratorConfig>>(`/registry/${id}`).then(r => r.data),
  getAssembledPrompt: (id: string) =>
    agentClient.get<ApiRes<AssembledPrompt>>(`/registry/${id}/assembled-prompt`).then(r => r.data),
  create: (data: Partial<OrchestratorConfig>) =>
    agentClient.post<ApiRes<OrchestratorConfig>>('/registry', data).then(r => r.data),
  update: (id: string, data: Partial<OrchestratorConfig>) =>
    agentClient.put<ApiRes<OrchestratorConfig>>(`/registry/${id}`, data).then(r => r.data),
  remove: (id: string) =>
    agentClient.delete<ApiRes<OrchestratorConfig>>(`/registry/${id}`).then(r => r.data),
};

export interface FrameworkAgentSummary {
  _id: string;
  id: string;
  instanceId: string;
  typeName: string;
  name: string;
  status: string;
  role?: string;
  description?: string;
  capabilities: string[];
  tier?: number;
  origin: 'user';
  isSystem: false;
  framework: true;
  createdAt?: string;
  lastActiveAt?: string;
  nextScheduledWake?: string;
}

export interface FrameworkAgentDetailData extends FrameworkAgentSummary {
  displayName: string;
  displayAliases: string[];
  goals: Record<string, unknown> | null;
}

export interface FrameworkAgentConfig {
  _id: string;
  name: string;
  system_prompt: string;
  resource_preferences: {
    default_model_tier: number;
    max_expert_agents: number;
    budget_priority: 'low' | 'medium' | 'high' | 'critical';
  };
  knowledge_entries: string[];
  voice_config: { blend: Array<{ voice_name: string; weight: number }> } | null;
  capabilities: string[];
  status: string;
}

export interface AgentToolEntry extends ToolCatalogEntry {
  access: 'required' | 'optional';
  resolved: boolean;
}

export const frameworkApi = {
  listAgents: () =>
    agentClient.get<ApiRes<FrameworkAgentSummary[]>>('/framework/agents').then(r => r.data),
  getAgent: (instanceId: string) =>
    agentClient.get<ApiRes<FrameworkAgentDetailData>>(`/framework/agents/${instanceId}`).then(r => r.data),
  updateAgent: (instanceId: string, data: { display_name?: string; display_aliases?: string[] }) =>
    agentClient.patch<ApiRes<{ instanceId: string; display_name: string; display_aliases: string[] }>>(`/framework/agents/${instanceId}`, data).then(r => r.data),
  getAgentConfig: (instanceId: string) =>
    agentClient.get<ApiRes<FrameworkAgentConfig>>(`/framework/agents/${instanceId}/config`).then(r => r.data),
  updateAgentConfig: (instanceId: string, data: Partial<Pick<FrameworkAgentConfig, 'system_prompt' | 'resource_preferences' | 'knowledge_entries' | 'voice_config'>>) =>
    agentClient.put<ApiRes<FrameworkAgentConfig>>(`/framework/agents/${instanceId}/config`, data).then(r => r.data),
  getAgentTools: (instanceId: string) =>
    agentClient.get<ApiRes<{ tools: AgentToolEntry[]; count: number }>>(`/framework/agents/${instanceId}/tools`).then(r => r.data),
  createAgent: (input: { description: string; scope?: string; scope_key?: string; parameters?: Record<string, unknown> }) =>
    agentClient.post<ApiRes<{ status: string; instance_id?: string; manifest_name?: string; summary?: string; missing?: Array<{ name: string; description?: string }> }>>('/framework/tools/create_agent/invoke', {
      description: input.description,
      scope: input.scope || 'user',
      scope_key: input.scope_key || '',
      parameters: input.parameters,
      approved: true,
    }).then(r => r.data),
};

// --- Events API ---

export const eventsApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<AgentEvent[]>>('/events', { params }).then(r => r.data),
  get: (id: string) =>
    agentClient.get<ApiRes<AgentEvent>>(`/events/${id}`).then(r => r.data),
  create: (data: Partial<AgentEvent>) =>
    agentClient.post<ApiRes<AgentEvent>>('/events', data).then(r => r.data),
  update: (id: string, data: Partial<AgentEvent>) =>
    agentClient.put<ApiRes<AgentEvent>>(`/events/${id}`, data).then(r => r.data),
  getActive: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<AgentEvent[]>>('/events/active', { params }).then(r => r.data),
};

// --- Memories API ---

export const memoriesApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<AgentMemory[]>>('/memories', { params }).then(r => r.data),
  get: (id: string) =>
    agentClient.get<ApiRes<AgentMemory>>(`/memories/${id}`).then(r => r.data),
  create: (data: Partial<AgentMemory>) =>
    agentClient.post<ApiRes<AgentMemory>>('/memories', data).then(r => r.data),
  update: (id: string, data: Partial<AgentMemory>) =>
    agentClient.put<ApiRes<AgentMemory>>(`/memories/${id}`, data).then(r => r.data),
  delete: (id: string) =>
    agentClient.delete<ApiRes<AgentMemory>>(`/memories/${id}`).then(r => r.data),
  recordUsage: (id: string) =>
    agentClient.post<ApiRes<AgentMemory>>(`/memories/${id}/usage`).then(r => r.data),
  byEvent: (eventId: string, params?: Record<string, string>) =>
    agentClient.get<ApiRes<AgentMemory[]>>(`/memories/by-event/${eventId}`, { params }).then(r => r.data),
  byAspects: (params: Record<string, string>) =>
    agentClient.get<ApiRes<AgentMemory[]>>('/memories/by-aspects', { params }).then(r => r.data),
  submitFeedback: (data: { thread_id: string; message_id: string; signal: 'positive' | 'negative'; correction_text?: string; memory_ids?: string[] }) =>
    agentClient.post<ApiRes<MemoryFeedbackResponse>>('/memories/feedback', data).then(r => r.data),
  getCorrections: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<AgentMemory[]>>('/memories/corrections', { params }).then(r => r.data),
  getChildren: (id: string) =>
    agentClient.get<ApiRes<AgentMemory[]>>(`/memories/${id}/children`).then(r => r.data),
  hybridSearch: (data: { query?: string; aspects?: string[]; community_id?: string; limit?: number }) =>
    agentClient.post<ApiRes<AgentMemory[]>>('/memories/hybrid-search', data).then(r => r.data),
};

// ============================================================================
// Phase 11 — User-Rooted Memory: Promotions, Reconciliation, Filings, Digest
// ============================================================================

export interface MemoryPromotion {
  promotion_id: string;
  source_memory_ids: string[];
  source_owner_user_id: string;
  target_organization_id: string;
  target_memory_id?: string;
  synthesized_text: string;
  llm_reasoning: string;
  llm_confidence: number;
  category: string;
  aspects: string[];
  status: 'pending_review' | 'auto_approved' | 'approved' | 'rejected' | 'revoked';
  decided_at: string;
  decided_by: string;
  reviewer_user_id?: string;
  reviewed_at?: string;
  reviewer_note?: string;
}

export interface ReconciliationProposal {
  proposal_id: string;
  user_id: string;
  scope_org_id: string | null;
  proposal_type:
    | 'likely_complete' | 'overdue' | 'stalled' | 'blocked'
    | 'forgotten_commitment' | 'cascade_ready' | 'proposed_goal_or_task';
  target_kind: 'task' | 'goal' | 'project' | 'new_project';
  target_id?: string;
  supporting_memory_ids: string[];
  proposed_change: Record<string, unknown>;
  llm_reasoning: string;
  llm_confidence: number;
  status: 'pending_review' | 'approved' | 'rejected' | 'expired';
  decided_at: string;
  decided_by: string;
}

export interface FilingProposal {
  filing_id: string;
  user_id: string;
  scope_org_id: string;
  kind: 'contact' | 'recurring_schedule' | 'one_off_event'
      | 'note_worthy' | 'expertise_domain' | 'expertise_location';
  source_memory_id: string;
  source_text_span?: number[];
  entity_payload: Record<string, unknown>;
  llm_confidence: number;
  status: 'pending_review' | 'approved' | 'rejected' | 'expired';
  decided_at: string;
  applied_target_id?: string;
}

export interface DigestTopItem {
  kind: 'task' | 'proposal' | 'promotion' | 'filing';
  id: string;
  title: string;
  urgency: 'high' | 'medium' | 'low';
  summary: string;
  proposal_id?: string;
}

export interface DailyDigest {
  digest_id: string;
  user_id: string;
  generated_at: string;
  for_date: string;
  sessions_run: number;
  total_tokens: number;
  extracted_count: number;
  merged_count: number;
  clarified_count: number;
  archived_count: number;
  promotions_auto: number;
  promotions_pending: number;
  overdue_count: number;
  likely_complete_count: number;
  stalled_count: number;
  forgotten_count: number;
  top_items: DigestTopItem[];
  per_org: Array<{
    organization_id: string | null;
    sessions_run: number;
    promotions_pending: number;
    overdue_count: number;
    extracted_count: number;
  }>;
  narrative_text: string;
  agent_voice: { agent_name?: string; personality_snippet?: string };
  delivered_to_chat_at?: string;
  delivered_message_id?: string;
  read_at?: string;
}

export const promotionsApi = {
  list: (scope: 'mine' | 'org' = 'mine', status?: string) =>
    agentClient
      .get<ApiRes<MemoryPromotion[]>>('/memories/promotions', { params: { scope, ...(status ? { status } : {}) } })
      .then(r => r.data),
  approve: (id: string, note?: string) =>
    agentClient.post<ApiRes<unknown>>(`/memories/promotions/${id}/approve`, { note }).then(r => r.data),
  reject: (id: string, note?: string) =>
    agentClient.post<ApiRes<unknown>>(`/memories/promotions/${id}/reject`, { note }).then(r => r.data),
  revoke: (id: string) =>
    agentClient.post<ApiRes<unknown>>(`/memories/promotions/${id}/revoke`).then(r => r.data),
};

export const reconciliationApi = {
  list: (scope: 'mine' | 'org' = 'mine', status?: string) =>
    agentClient
      .get<ApiRes<ReconciliationProposal[]>>('/reconciliation/proposals', { params: { scope, ...(status ? { status } : {}) } })
      .then(r => r.data),
  approve: (id: string, note?: string) =>
    agentClient.post<ApiRes<unknown>>(`/reconciliation/proposals/${id}/approve`, { note }).then(r => r.data),
  reject: (id: string, note?: string) =>
    agentClient.post<ApiRes<unknown>>(`/reconciliation/proposals/${id}/reject`, { note }).then(r => r.data),
};

export const filingsApi = {
  list: (status?: string) =>
    agentClient
      .get<ApiRes<FilingProposal[]>>('/filings', { params: status ? { status } : {} })
      .then(r => r.data),
  approve: (id: string, note?: string) =>
    agentClient.post<ApiRes<unknown>>(`/filings/${id}/approve`, { note }).then(r => r.data),
  reject: (id: string, note?: string) =>
    agentClient.post<ApiRes<unknown>>(`/filings/${id}/reject`, { note }).then(r => r.data),
};

export const digestApi = {
  list: (from?: string, to?: string, limit = 30, skip = 0) =>
    agentClient
      .get<ApiRes<DailyDigest[]>>('/digest', {
        params: { ...(from ? { from } : {}), ...(to ? { to } : {}), limit, skip },
      })
      .then(r => r.data),
  latest: () => agentClient.get<ApiRes<DailyDigest | null>>('/digest/latest').then(r => r.data),
  get: (id: string) => agentClient.get<ApiRes<DailyDigest>>(`/digest/${id}`).then(r => r.data),
  byDate: (date: string) => agentClient.get<ApiRes<DailyDigest | null>>(`/digest/by-date/${date}`).then(r => r.data),
  markRead: (id: string) => agentClient.post<ApiRes<unknown>>(`/digest/${id}/read`).then(r => r.data),
};

// --- Address Book API ---

export interface AddressBookEntry {
  entry_id: string;
  user_id: string;
  organization_id: string;
  contact_id: string;
  contact_type: 'user' | 'agent' | 'system';
  contact_name: string;
  relationship_context: string;
  interrupt_priority: 'always' | 'waking-hours' | 'business-hours' | 'never' | 'context-dependent';
  escalation_steps: Array<{ action: string; delayMinutes: number }>;
  preferred_channel?: string;
  dnd_override: boolean;
  critical_keywords: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
}

export const addressBookApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<AddressBookEntry[]>>('/address-book', { params }).then(r => r.data),
  get: (contactId: string) =>
    agentClient.get<ApiRes<AddressBookEntry>>(`/address-book/${contactId}`).then(r => r.data),
  upsert: (data: Partial<AddressBookEntry>) =>
    agentClient.post<ApiRes<AddressBookEntry>>('/address-book', data).then(r => r.data),
  remove: (contactId: string) =>
    agentClient.delete<ApiRes<void>>(`/address-book/${contactId}`).then(r => r.data),
  seed: () =>
    agentClient.post<ApiRes<{ seeded: number }>>('/address-book/seed').then(r => r.data),
};

// --- Schedule API ---

export interface ScheduleEntry {
  entry_id: string;
  user_id: string;
  organization_id: string;
  entry_type: 'explicit' | 'imported' | 'learned-pattern' | 'inferred';
  title: string;
  description?: string;
  availability_status: 'available' | 'busy' | 'sleeping' | 'social' | 'focused' | 'dnd';
  confidence: 'high' | 'medium' | 'low';
  start_time?: string;
  end_time?: string;
  recurrence?: { days_of_week: number[]; start_hour: string; end_hour: string; timezone: string };
  observation_count: number;
  confirmed_by_user: boolean;
  max_interrupt_urgency: string;
  created_at: string;
  updated_at: string;
}

export interface AvailabilityResult {
  status: string;
  context?: string;
  maxInterruptUrgency?: string;
}

export const scheduleApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<ScheduleEntry[]>>('/schedule', { params }).then(r => r.data),
  getAvailability: () =>
    agentClient.get<ApiRes<AvailabilityResult>>('/schedule/availability').then(r => r.data),
  create: (data: Partial<ScheduleEntry>) =>
    agentClient.post<ApiRes<ScheduleEntry>>('/schedule', data).then(r => r.data),
  update: (entryId: string, data: Partial<ScheduleEntry>) =>
    agentClient.put<ApiRes<ScheduleEntry>>(`/schedule/${entryId}`, data).then(r => r.data),
  confirm: (entryId: string) =>
    agentClient.post<ApiRes<void>>(`/schedule/${entryId}/confirm`).then(r => r.data),
  remove: (entryId: string) =>
    agentClient.delete<ApiRes<void>>(`/schedule/${entryId}`).then(r => r.data),
};

// --- Notes API ---

export interface AgentNote {
  note_id: string;
  user_id: string;
  organization_id: string;
  title: string;
  content: string;
  tags: string[];
  owner: 'user' | 'agent' | 'shared';
  pinned: boolean;
  created_at: string;
  updated_at: string;
  last_accessed_at: string;
}

export const notesApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<AgentNote[]>>('/notes', { params }).then(r => r.data),
  get: (noteId: string) =>
    agentClient.get<ApiRes<AgentNote>>(`/notes/${noteId}`).then(r => r.data),
  create: (data: Partial<AgentNote>) =>
    agentClient.post<ApiRes<AgentNote>>('/notes', data).then(r => r.data),
  update: (noteId: string, data: Partial<AgentNote>) =>
    agentClient.put<ApiRes<AgentNote>>(`/notes/${noteId}`, data).then(r => r.data),
  append: (noteId: string, text: string) =>
    agentClient.post<ApiRes<AgentNote>>(`/notes/${noteId}/append`, { text }).then(r => r.data),
  search: (q: string) =>
    agentClient.get<ApiRes<AgentNote[]>>('/notes/search', { params: { q } }).then(r => r.data),
  remove: (noteId: string) =>
    agentClient.delete<ApiRes<void>>(`/notes/${noteId}`).then(r => r.data),
};

// --- Resources API ---

export const resourcesApi = {
  getCurrentBudget: () =>
    agentClient.get<ApiRes<ResourceBudget>>('/resources/budgets/current').then(r => r.data),
  createBudget: (data: Partial<ResourceBudget>) =>
    agentClient.post<ApiRes<ResourceBudget>>('/resources/budgets', data).then(r => r.data),
  allocate: (budgetId: string, data: ResourceBudgetAllocationRequest) =>
    agentClient.put<ApiRes<ResourceBudget>>(`/resources/budgets/${budgetId}/allocate`, data).then(r => r.data),
  getSummary: () =>
    agentClient.get<ApiRes<ResourceSummaryData>>('/resources/summary').then(r => r.data),
  getUsage: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<ResourceUsageLog[]>>('/resources/usage', { params }).then(r => r.data),
  getUsageByTier: () =>
    agentClient.get<ApiRes<ResourceUsageByTierEntry[]>>('/resources/usage/by-tier').then(r => r.data),
  getSpending: (days?: number) =>
    agentClient.get<ApiRes<SpendingSummary>>('/resources/usage/spending', { params: days ? { days: String(days) } : undefined }).then(r => r.data),
};

// --- Communities API ---

export const communitiesApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<Community[]>>('/communities', { params }).then(r => r.data),
  get: (id: string) =>
    agentClient.get<ApiRes<Community>>(`/communities/${id}`).then(r => r.data),
  create: (data: Partial<Community>) =>
    agentClient.post<ApiRes<Community>>('/communities', data).then(r => r.data),
  getMine: () =>
    agentClient.get<ApiRes<Community>>('/communities/mine').then(r => r.data),
  getSystem: () =>
    agentClient.get<ApiRes<Community[]>>('/communities/system').then(r => r.data),
};

// --- Chat API ---

export interface ChatResponse {
  response: string;
  usage: {
    tokensInput: number;
    tokensOutput: number;
    estimatedCostUsd: number;
    modelName: string;
    modelTier: number;
  };
  threadId: string;
  memoriesCreated: number;
  eventsCreated: number;
  metadata?: {
    data?: unknown[];
    visualization?: { type: string; config: Record<string, unknown> };
    source?: { id: string; name: string };
    followUpSuggestions?: string[];
    memoriesUsed?: string[];
  };
  warnings?: string[];
  userAgentQuestion?: string | null;
  pendingTaskId?: string;
  agentName?: string;
}

export interface ChatStatus {
  available: boolean;
  orchestratorCount: number;
  orchestrators: Array<{ name: string; capabilities: string[] }>;
}

export interface ChatThread {
  thread_id: string;
  title?: string;
  status: ThreadStatus;
  turn_count: number;
  created_at: string;
  updated_at: string;
}

export interface ChatMessageFromServer {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  usage?: {
    tokensInput: number;
    tokensOutput: number;
    estimatedCostUsd: number;
    modelName: string;
    modelTier: number;
  };
}

export const chatApi = {
  send: (message: string, community_id?: string, thread_id?: string) =>
    agentClient.post<ApiRes<ChatResponse>>('/chat', { message, community_id, thread_id }).then(r => r.data),
  getStatus: () =>
    agentClient.get<ApiRes<ChatStatus>>('/chat/status').then(r => r.data),
  listThreads: (limit?: number, offset?: number) =>
    agentClient.get<ApiRes<{ threads: ChatThread[]; total: number }>>('/chat/threads', {
      params: { ...(limit && { limit: String(limit) }), ...(offset && { offset: String(offset) }) },
    }).then(r => r.data),
  getThreadMessages: (threadId: string) =>
    agentClient.get<ApiRes<{ threadId: string; title: string; messages: ChatMessageFromServer[] }>>(
      `/chat/threads/${threadId}/messages`
    ).then(r => r.data),
};

// --- Visual Editor API ---

export interface EditorContext {
  projectId?: string;
  schemaVersion: 1 | 2;
  contextBuiltAtStep: number;
  pageId: string;
  breakpoint: 'mobile' | 'tablet' | 'desktop';
  colorScheme?: ColorScheme;
  styleWriteTarget?: ColorSchemeWriteTarget;
  components: Array<{
    id: string;
    type: string;
    name: string;
    semanticLabel?: string;
    hierarchyDepth?: number;
    parentId?: string;
    children?: string[];
    properties?: Record<string, unknown>;
    style?: Record<string, unknown>;
    breakpointStyles?: Record<string, unknown>;
    size?: { width: number; height: number };
    bindingContext?: AiBindingContextSummary;
  }>;
  selectedComponentIds: string[];
  canvasWidth: number;
  canvasHeight: number;
  sessionContext?: string;
  dataBindingInstructions?: AiBindingInstructionSurface;
  dataBindingSummary?: {
    boundCount: number;
    referencedSchemas: string[];
  };
  projectDataBindingSummary?: {
    boundCount: number;
    referencedSchemas: string[];
  };
  totalPages?: number;
  pages?: Array<{
    id: string;
    name: string;
    isCurrent: boolean;
    componentCount: number;
    dataBoundCount?: number;
    referencedSchemas?: string[];
    topTypes?: Array<{ type: string; count: number }>;
    sampleComponents?: Array<{
      id: string;
      type: string;
      name: string;
      semanticLabel?: string;
      hasDataSource?: boolean;
    }>;
  }>;
  tradeScope?: string | null;
}

export interface CanvasAction {
  type: 'add_component' | 'update_component' | 'delete_component' | 'add_page' | 'update_page' | 'reorder_children' | 'batch_update' | 'replace_page' | 'switch_page' | 'link_pages' | 'create_branch' | 'move_component' | 'preflight_pm_write_target';
  payload: {
    componentId?: string;
    pageId?: string;
    component?: Record<string, unknown>;
    changes?: Record<string, unknown>;
    components?: Array<Record<string, unknown>>;
    parentId?: string | null;
    position?: number;
    breakpoint?: 'mobile' | 'tablet' | 'desktop';
    newOrder?: string[];
    // Multi-page fields
    pageName?: string;
    pageDescription?: string;
    pageOrder?: number;
    targetPageIds?: string[];
    navigationConfig?: Record<string, unknown>;
    // Branching fields
    branchName?: string;
    branchDescription?: string;
    // Move component fields
    oldParentId?: string;
    newParentId?: string | null;
    // Phase 11C PM write-target preflight fields
    operation?: ComponentDataWriteOperation;
    writeTarget?: unknown;
    writeTargets?: unknown[];
    candidateWriteTargets?: unknown[];
    candidates?: unknown[];
    operations?: Partial<Record<ComponentDataWriteOperation, boolean>>;
    selectedOrganizationId?: string;
    currentOrganizationId?: string;
    capabilities?: { writable?: boolean };
    permissionMode?: 'read-only' | 'read-write';
    // Flat format fallback — some LLM outputs use payload.id instead of payload.componentId
    id?: string;
  };
  metadata?: {
    agentId: string;
    agentDomain: string;
    reasoning?: string;
    confidence?: number;
  };
}

// Reasoning transparency types
export interface ReasoningStep {
  agent: string;
  step: string;
  reasoning: string;
  alternatives?: Array<{
    description: string;
    whyNotChosen: string;
  }>;
  confidence: number;
  memoryInfluence?: string[];
  duration_ms?: number;
}

// Proactive suggestion types
export interface ProactiveSuggestion {
  id: string;
  category: 'accessibility' | 'brand-compliance' | 'layout' | 'data-opportunity' | 'performance' | 'ux';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  description: string;
  reasoning?: string;
  confidence: number;
  suggestedActions?: CanvasAction[];
  affectedComponentIds?: string[];
  wcagCriteria?: string;
}

// Accessibility audit types
export interface AccessibilityViolation {
  componentId: string;
  componentName?: string;
  rule: string;
  wcagCriteria: string;
  severity: 'minor' | 'moderate' | 'serious' | 'critical';
  description: string;
  fixActions?: CanvasAction[];
}

export interface AccessibilityAuditResult {
  score: number;
  totalChecks: number;
  passes: number;
  violations: AccessibilityViolation[];
  summary: string;
}

export interface AgentVisualResponse {
  actions?: CanvasAction[];  // Optional: not present in SSE complete events (Phase 6b dedup)
  message: string;
  followUpSuggestions?: string[];
  requiresConfirmation?: boolean;
  threadId?: string;
  memoriesCreated?: number;
  memoriesReinforced?: number;
  reasoningChain?: ReasoningStep[];
  proactiveSuggestions?: ProactiveSuggestion[];
  accessibilityAudit?: AccessibilityAuditResult;
}

export const visualEditorApi = {
  execute: (message: string, editorContext: EditorContext, thread_id?: string) =>
    agentClient.post<ApiRes<AgentVisualResponse>>('/visual-editor/execute', {
      message, editorContext, thread_id,
    }).then(r => r.data),
  feedback: (threadId: string, signal: 'positive' | 'negative', correctionText?: string, componentIds?: string[]) =>
    agentClient.post<ApiRes<{ success: boolean }>>('/visual-editor/feedback', {
      thread_id: threadId, signal, correction_text: correctionText, component_ids: componentIds,
    }).then(r => r.data),
  invalidateCache: (orgId: string) =>
    agentClient.delete<ApiRes<{ keysDeleted: number }>>(`/visual-editor/cache/${orgId}`).then(r => r.data),
  getCacheMetrics: () =>
    agentClient.get<ApiRes<{ hits: number; misses: number; sets: number; hitRate: number }>>('/visual-editor/cache/metrics').then(r => r.data),
  getProjectLocks: (projectId: string) =>
    agentClient.get<ApiRes<{ locks: Array<{ projectId: string; pageId: string; userId: string; userName?: string; acquiredAt: string; expiresAt: string }> }>>(`/visual-editor/locks/${projectId}`).then(r => r.data),
  // Phase 6: Reasoning override feedback
  postReasoningOverride: (threadId: string, componentId: string, originalReasoning: string, userAction: string) =>
    agentClient.post<ApiRes<{ success: boolean }>>('/visual-editor/reasoning-feedback', {
      threadId, componentId, originalReasoning, userAction,
    }).then(r => r.data),
  // Phase 6: Proactive design analysis
  analyze: (editorContext: EditorContext) =>
    agentClient.post<ApiRes<{ suggestions: ProactiveSuggestion[] }>>('/visual-editor/analyze', {
      editorContext,
    }).then(r => r.data),
  // Phase 6: Suggestion feedback
  suggestionFeedback: (suggestionId: string, action: 'apply' | 'dismiss' | 'helpful' | 'unhelpful') =>
    agentClient.post<ApiRes<{ success: boolean }>>('/visual-editor/suggestion-feedback', {
      suggestionId, action,
    }).then(r => r.data),
  // Phase 6: Accessibility audit
  auditAccessibility: (editorContext: EditorContext) =>
    agentClient.post<ApiRes<AccessibilityAuditResult>>('/visual-editor/audit/accessibility', {
      editorContext,
    }).then(r => r.data),
  // Phase 6: Shared patterns opt-in
  getSharedPatternsOptIn: () =>
    agentClient.get<ApiRes<{ optedIn: boolean }>>('/visual-editor/shared-patterns/opt-in').then(r => r.data),
  setSharedPatternsOptIn: (optIn: boolean) =>
    agentClient.put<ApiRes<{ optedIn: boolean }>>('/visual-editor/shared-patterns/opt-in', { optIn }).then(r => r.data),
  // Debug: Agent health check
  getAgentHealth: () =>
    agentClient.get<ApiRes<{
      mqttConnected: boolean;
      agents: Record<string, { initialized: boolean }>;
      timestamp: number;
    }>>('/visual-editor/agent-health').then(r => r.data),
  // Phase 4: LLM-based intent classification for routing
  classifyRoute: (message: string, context: { hasSelectedComponent: boolean; hasSelectedComponentType?: string; agentAvailable: boolean; hasSessionContext: boolean }) =>
    agentClient.post<{ success: boolean; route?: string; isFollowUp?: boolean; complexity?: string; mentionedBreakpoints?: string[]; followUpAgent?: string | null; confidence?: number; error?: string }>(
      '/visual-editor/classify-route',
      { message, context },
      { timeout: 800 },
    ).then(r => r.data),
};

// --- Plan Mode API ---

/** Trim conversation history for API payloads to prevent unbounded sizes */
function trimConversationHistory(
  history: Array<{ role: string; content: string }>,
  maxTurns = 6,
  maxChars = 4000,
): Array<{ role: string; content: string }> {
  let trimmed = history.slice(-maxTurns);
  let totalChars = trimmed.reduce((sum, m) => sum + m.content.length, 0);
  while (totalChars > maxChars && trimmed.length > 2) {
    trimmed = trimmed.slice(1);
    totalChars = trimmed.reduce((sum, m) => sum + m.content.length, 0);
  }
  return trimmed;
}

export const planApi = {
  /** Single planning conversation turn (both modes) */
  converse: (
    message: string,
    conversationHistory: Array<{ role: string; content: string }>,
    editorContext?: EditorContext,
    thread_id?: string,
    researchContext?: PlanResearchContext | null,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<PlanConversationResponse>>(
      `/visual-editor/plan/converse${debug ? '?debug=true' : ''}`,
      { message, conversationHistory: trimConversationHistory(conversationHistory), editorContext, thread_id, researchContext: researchContext ?? undefined },
      { signal },
    ).then(r => r.data),

  /** Skill wizard conversation turn */
  skillConverse: (
    message: string,
    conversationHistory: Array<{ role: string; content: string }>,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<SkillWizardResponse>>(
      `/visual-editor/plan/skill/converse${debug ? '?debug=true' : ''}`,
      { message, conversationHistory: trimConversationHistory(conversationHistory), thread_id },
      { signal },
    ).then(r => r.data),

  /** Agent wizard conversation turn */
  agentConverse: (
    message: string,
    conversationHistory: Array<{ role: string; content: string }>,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<AgentWizardResponse>>(
      `/visual-editor/plan/agent/converse${debug ? '?debug=true' : ''}`,
      { message, conversationHistory: trimConversationHistory(conversationHistory), thread_id },
      { signal },
    ).then(r => r.data),

  /** Knowledge wizard conversation turn */
  knowledgeConverse: (
    message: string,
    conversationHistory: Array<{ role: string; content: string }>,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<KnowledgeWizardResponse>>(
      `/visual-editor/plan/knowledge/converse${debug ? '?debug=true' : ''}`,
      { message, conversationHistory: trimConversationHistory(conversationHistory), thread_id },
      { signal },
    ).then(r => r.data),

  /** Workflow wizard conversation turn */
  workflowConverse: (
    message: string,
    conversationHistory: Array<{ role: string; content: string }>,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
    knowledgeContext?: { title: string; description: string; domain: string; articles?: Array<{ title: string; outline: string }>; tags?: string[] },
    schemaContext?: Array<{ name: string; groupName: string; fields: Array<{ name: string; type: string }> }>,
    componentContext?: Array<{ id: string; type: string; name?: string; page?: string }>,
  ) =>
    agentClient.post<ApiRes<WorkflowWizardResponse>>(
      `/visual-editor/plan/workflow/converse${debug ? '?debug=true' : ''}`,
      { message, conversationHistory: trimConversationHistory(conversationHistory), thread_id, knowledgeContext, schemaContext, componentContext },
      { signal },
    ).then(r => r.data),

  /** Data schema wizard conversation turn */
  dataConverse: (
    message: string,
    conversationHistory: Array<{ role: string; content: string }>,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
    knowledgeContext?: { title: string; description: string; domain: string; articles?: Array<{ title: string; outline: string }>; tags?: string[] },
  ) =>
    agentClient.post<ApiRes<DataWizardResponse>>(
      `/visual-editor/plan/data/converse${debug ? '?debug=true' : ''}`,
      { message, conversationHistory: trimConversationHistory(conversationHistory), thread_id, knowledgeContext },
      { signal },
    ).then(r => r.data),

  /** Page design wizard conversation turn */
  pageDesignConverse: (
    message: string,
    conversationHistory: Array<{ role: string; content: string }>,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
    knowledgeContext?: { title: string; description: string; domain: string;
      articles?: Array<{ title: string; outline: string }>; tags?: string[] },
    schemaContext?: Array<{ name: string; groupName: string;
      fields: Array<{ name: string; type: string }> }>,
  ) =>
    agentClient.post<ApiRes<PageDesignWizardResponse>>(
      `/visual-editor/plan/page-design/converse${debug ? '?debug=true' : ''}`,
      { message, conversationHistory: trimConversationHistory(conversationHistory),
        thread_id, knowledgeContext, schemaContext },
      { signal },
    ).then(r => r.data),

  /** Classify wizard intent via LLM (Tier 2, stateless) */
  classifyWizardIntent: (message: string) =>
    agentClient.post<{ success: boolean; intent: string; confidence: number; domainHint: string | null }>(
      '/visual-editor/plan/classify-wizard-intent',
      { message },
      { timeout: 800 },
    ).then(r => r.data),

  /** Compile conversation into structured plan (Quick Mode) */
  compile: (
    conversationHistory: Array<{ role: string; content: string }>,
    editorContext?: EditorContext,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
    schemaContext?: Array<{ name: string; groupName: string;
      fields: Array<{ name: string; type: string; required?: boolean; description?: string }> }>,
    knowledgeContext?: { title: string; description: string; domain: string;
      articles?: Array<{ title: string; outline: string }>; tags?: string[] },
  ) =>
    agentClient.post<ApiRes<PlanCompilationResponse>>(
      `/visual-editor/plan/compile${debug ? '?debug=true' : ''}`,
      { message: 'Compile plan from conversation', conversationHistory: trimConversationHistory(conversationHistory), editorContext, thread_id, schemaContext, knowledgeContext },
      { signal },
    ).then(r => r.data),

  /** Conduct web research (Full Mode) */
  research: (
    topics: string[],
    conversationHistory: Array<{ role: string; content: string }>,
    thread_id?: string,
    signal?: AbortSignal,
  ) =>
    agentClient.post<ApiRes<{ researchContext: PlanResearchContext }>>(
      '/visual-editor/plan/research',
      { suggestions: topics, conversationHistory: trimConversationHistory(conversationHistory), thread_id },
      { signal },
    ).then(r => r.data),

  /** Generate structured roadmap (Full Mode) */
  generateRoadmap: (
    conversationHistory: Array<{ role: string; content: string }>,
    researchContext: PlanResearchContext | null,
    editorContext?: EditorContext,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<{ roadmap: Roadmap }>>(
      `/visual-editor/plan/roadmap/generate${debug ? '?debug=true' : ''}`,
      { conversationHistory: trimConversationHistory(conversationHistory), researchContext, editorContext, thread_id },
      { signal },
    ).then(r => r.data),

  /** Regenerate roadmap with refinement or audit feedback (Full Mode) */
  regenerateRoadmap: (
    previousRoadmap: Roadmap,
    refinementFeedback: string,
    conversationHistory: Array<{ role: string; content: string }>,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
    source?: 'audit' | 'user',
  ) =>
    agentClient.post<ApiRes<{ roadmap: Roadmap }>>(
      `/visual-editor/plan/roadmap/regenerate${debug ? '?debug=true' : ''}`,
      { previousRoadmap, refinementFeedback, conversationHistory: trimConversationHistory(conversationHistory), thread_id, source },
      { signal },
    ).then(r => r.data),

  /** Audit generated roadmap (Full Mode) */
  auditRoadmap: (
    roadmap: Roadmap,
    conversationHistory: Array<{ role: string; content: string }>,
    researchContext?: PlanResearchContext,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<{ auditResult: AuditResult }>>(
      `/visual-editor/plan/roadmap/audit${debug ? '?debug=true' : ''}`,
      { roadmap, conversationHistory: trimConversationHistory(conversationHistory), researchContext },
      { signal },
    ).then(r => r.data),

  /** Decompose roadmap into phase prompts (Full Mode) */
  decomposePhases: (
    roadmap: Roadmap,
    researchContext?: PlanResearchContext,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<{ phases: PhasePrompt[] }>>(
      `/visual-editor/plan/phases/decompose${debug ? '?debug=true' : ''}`,
      { roadmap, researchContext },
      { signal },
    ).then(r => r.data),

  /** Audit all phase prompts (Full Mode) */
  auditPhases: (
    phases: PhasePrompt[],
    roadmap: Roadmap,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<{ phaseAudits: Record<string, AuditResult> }>>(
      `/visual-editor/plan/phases/audit${debug ? '?debug=true' : ''}`,
      { phases, roadmap },
      { signal },
    ).then(r => r.data),

  /** Execute single phase with SSE streaming — returns raw fetch Response */
  executePhaseStream: async (
    phaseId: string,
    executionPrompt: string,
    domain: 'visual-editor' | 'interaction' | 'data' | 'workflow',
    editorContext?: EditorContext,
    thread_id?: string,
    signal?: AbortSignal,
  ): Promise<Response> => {
    const tokens = getCurrentAuthTokens();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    };
    if (tokens?.accessToken) {
      headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }
    Object.assign(headers, await getValidatedStoredOrganizationHeaderValues());

    return fetch(
      `${appConfig.api.agentServiceUrl}/api/v1/visual-editor/plan/phases/${encodeURIComponent(phaseId)}/execute/stream`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phasePrompt: { executionPrompt, domain },
          domain,
          editorContext,
          thread_id,
          projectId: editorContext?.projectId,
        }),
        signal,
      },
    );
  },

  /** Execute data/workflow phase via LiaisonAgent (non-streaming) */
  executePhaseLiaison: (
    phaseId: string,
    executionPrompt: string,
    domain: 'data' | 'workflow',
    editorContext?: EditorContext,
    thread_id?: string,
    signal?: AbortSignal,
  ) =>
    agentClient.post<ApiRes<LiaisonPhaseExecutionResponse>>(
      `/visual-editor/plan/phases/${encodeURIComponent(phaseId)}/execute/liaison`,
      { executionPrompt, domain, editorContext, thread_id },
      { signal },
    ).then(r => r.data),

  /** Regenerate a phase prompt using audit feedback via LLM */
  regeneratePhase: (
    phaseId: string,
    auditFeedback: AuditResult,
    roadmap: Roadmap,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<RegeneratePhaseResponse>>(
      `/visual-editor/plan/phases/${encodeURIComponent(phaseId)}/regenerate${debug ? '?debug=true' : ''}`,
      { auditFeedback, roadmap },
      { signal },
    ).then(r => r.data),

  /** Audit single phase implementation (Full Mode) */
  auditImplementation: (
    phaseId: string,
    actions: unknown[],
    phasePrompt: { prompt?: string; executionPrompt?: string; domain?: string },
    priorArtifacts?: Record<string, string[]>,
    thread_id?: string,
    signal?: AbortSignal,
    debug?: boolean,
    groundingContext?: Record<string, unknown>,
  ) =>
    agentClient.post<ApiRes<{ auditResult: AuditResult }>>(
      `/visual-editor/plan/phases/${encodeURIComponent(phaseId)}/audit-implementation${debug ? '?debug=true' : ''}`,
      { phasePrompt, actions, priorArtifacts, thread_id, groundingContext },
      { signal },
    ).then(r => r.data),

  /** Final holistic audit (Full Mode) */
  auditFinal: (
    allPhaseResults: Array<{ phaseId: string; actions: unknown[]; artifacts: Record<string, string[]> }>,
    roadmap: Roadmap,
    phaseAudits: Array<{ phaseId: string; verdict: string; score: number; issues: string[] }>,
    signal?: AbortSignal,
    debug?: boolean,
  ) =>
    agentClient.post<ApiRes<{ auditResult: AuditResult }>>(
      `/visual-editor/plan/audit/final${debug ? '?debug=true' : ''}`,
      { allPhaseResults, roadmap, phaseAudits },
      { signal },
    ).then(r => r.data),

  /** Save roadmap to backend (auto-save or manual) */
  saveRoadmap: (
    roadmap: Roadmap,
    auditResult?: AuditResult,
  ) =>
    agentClient.post<ApiRes<{ id: string }>>(
      '/visual-editor/plan/roadmap/save',
      { roadmap, auditResult },
    ).then(r => r.data),

  /** List saved roadmaps for the organization */
  listSavedRoadmaps: () =>
    agentClient.get<ApiRes<{ roadmaps: SavedRoadmapSummary[] }>>(
      '/visual-editor/plan/roadmaps',
    ).then(r => r.data),

  /** Get full saved roadmap by ID */
  getSavedRoadmap: (id: string) =>
    agentClient.get<ApiRes<{ roadmap: Roadmap; auditResult: AuditResult | null }>>(
      `/visual-editor/plan/roadmap/saved/${encodeURIComponent(id)}`,
    ).then(r => r.data),

  /** Delete (archive) a saved roadmap */
  deleteSavedRoadmap: (id: string) =>
    agentClient.delete<ApiRes<Record<string, never>>>(
      `/visual-editor/plan/roadmap/saved/${encodeURIComponent(id)}`,
    ).then(r => r.data),
};

// --- Prompt Versions API ---

export interface PromptVersionSummary {
  agentName: string;
  version: number;
  promptHash: string;
  changedAt: string;
  metrics: { requestCount: number; avgLatencyMs: number; avgCostUsd: number; errorRate: number };
}

export interface PromptVersionDetail extends PromptVersionSummary {
  promptText: string;
  changedBy: string;
}

export const promptsApi = {
  listVersions: (agentName?: string) =>
    agentClient.get<ApiRes<{ versions: PromptVersionSummary[] }>>('/visual-editor/prompts', { params: agentName ? { agentName } : {} }).then(r => r.data),
  getVersion: (agentName: string, version: number) =>
    agentClient.get<ApiRes<PromptVersionDetail>>(`/visual-editor/prompts/${encodeURIComponent(agentName)}/${version}`).then(r => r.data),
};

// --- Traces API ---

export type ThreadInitiator = 'user' | 'schedule' | 'trigger' | 'agent' | 'data_source' | 'device' | 'ui_action';
export type ThreadStatus = 'active' | 'completed' | 'error';
export type SpanKind =
  | 'user_message' | 'final_response' | 'agent_routing' | 'agent_execution'
  | 'llm_call' | 'knowledge_retrieval' | 'memory_access' | 'memory_write'
  | 'mqtt_publish' | 'mqtt_wait' | 'data_query' | 'api_call' | 'evaluation' | 'error'
  // Unified tracing span kinds
  | 'ui_data_request' | 'workflow_execution' | 'workflow_node'
  | 'ui_data_render' | 'ui_action' | 'device_data_received';

export interface ThreadSummary {
  thread_id: string;
  organization_id: string;
  user_id?: string;
  initiator: ThreadInitiator;
  initiator_detail?: string;
  status: ThreadStatus;
  title?: string;
  agent_route?: string;
  turn_count: number;
  total_spans: number;
  total_llm_calls: number;
  total_tokens: number;
  total_cost_usd: number;
  total_duration_ms: number;
  trace_level: number;
  created_at: string;
  updated_at: string;
}

export interface SpanDetail {
  span_id: string;
  thread_id: string;
  parent_span_id?: string;
  turn_index: number;
  kind: SpanKind;
  agent: string;
  status: 'running' | 'ok' | 'error';
  trace_level: number;
  summary: string;
  detail?: Record<string, unknown>;
  llm?: {
    model: string;
    tier: number;
    provider: string;
    prompt_system?: string;
    prompt_user?: string;
    response?: string;
    tokens_input: number;
    tokens_output: number;
    cost_usd: number;
    temperature?: number;
    max_tokens?: number;
    latency_ms: number;
  };
  started_at: string;
  ended_at?: string;
  duration_ms?: number;
  error_message?: string;
  children?: SpanDetail[];
}

export interface TraceStats {
  totalThreads: number;
  totalLLMCalls: number;
  totalTokens: number;
  totalCost: number;
  avgDurationMs: number;
  byInitiator: Record<string, number>;
  byAgent: Record<string, number>;
}

export interface TraceLevelConfig {
  scope: 'system' | 'organization' | 'user';
  scope_id: string;
  trace_level: number;
  updated_by: string;
}

export interface ThreadFilters {
  limit?: number;
  offset?: number;
  initiator?: ThreadInitiator;
  status?: ThreadStatus;
  agentRoute?: string;
  fromDate?: string;
  toDate?: string;
}

export const tracesApi = {
  listThreads: (params?: ThreadFilters) => {
    const query: Record<string, string> = {};
    if (params) {
      const {
        limit,
        offset,
        initiator,
        status,
        agentRoute,
        fromDate,
        toDate,
      } = params;
      if (limit !== undefined) query.limit = String(limit);
      if (offset !== undefined) query.offset = String(offset);
      if (initiator) query.initiator = initiator;
      if (status) query.status = status;
      if (agentRoute) query.agentRoute = agentRoute;
      if (fromDate) query.fromDate = fromDate;
      if (toDate) query.toDate = toDate;
    }
    return agentClient.get<ApiRes<{ threads: ThreadSummary[]; total: number }>>('/traces/threads', { params: query }).then(r => r.data);
  },
  getThread: (threadId: string) =>
    agentClient.get<ApiRes<ThreadSummary>>(`/traces/threads/${threadId}`).then(r => r.data),
  getSpans: (threadId: string, turn?: number) => {
    const params: Record<string, string> = {};
    if (turn !== undefined) params.turn = String(turn);
    return agentClient.get<ApiRes<SpanDetail[]>>(`/traces/threads/${threadId}/spans`, { params }).then(r => r.data);
  },
  getSpan: (spanId: string) =>
    agentClient.get<ApiRes<SpanDetail>>(`/traces/spans/${spanId}`).then(r => r.data),
  getStats: (days?: number) => {
    const params: Record<string, string> = {};
    if (days !== undefined) params.days = String(days);
    return agentClient.get<ApiRes<TraceStats>>('/traces/stats', { params }).then(r => r.data);
  },
  getConfig: () =>
    agentClient.get<ApiRes<TraceLevelConfig[]>>('/traces/config').then(r => r.data),
  setConfig: (data: { scope: string; scopeId: string; traceLevel: number }) =>
    agentClient.put<ApiRes<TraceLevelConfig>>('/traces/config', data).then(r => r.data),
  deleteConfig: (scope: string, scopeId: string) =>
    agentClient.delete<ApiRes<void>>(`/traces/config/${scope}/${scopeId}`).then(r => r.data),

  // AI-consumable summary
  getThreadSummary: (threadId: string) =>
    agentClient.get<ApiRes<Record<string, unknown>>>(`/traces/threads/${threadId}/summary`).then(r => r.data),

  // Write endpoints (for TraceClient)
  getEffectiveTraceLevel: () =>
    agentClient.get<ApiRes<{ traceLevel: number }>>('/traces/config/effective').then(r => r.data),
  createThread: (params: { initiator: ThreadInitiator; initiatorDetail?: string; title?: string; metadata?: Record<string, unknown> }) =>
    agentClient.post<ApiRes<{ thread_id: string; trace_level: number }>>('/traces/threads', params).then(r => r.data),
  createSpan: (params: { threadId: string; parentSpanId?: string; turnIndex?: number; kind: SpanKind; agent: string; traceLevel?: number; summary: string; detail?: Record<string, unknown> }) =>
    agentClient.post<ApiRes<{ span_id: string | null }>>('/traces/spans', params).then(r => r.data),
  endSpan: (spanId: string, params: { status: 'ok' | 'error'; summary?: string; detail?: Record<string, unknown>; errorMessage?: string }) =>
    agentClient.put<ApiRes<void>>(`/traces/spans/${spanId}/end`, params).then(r => r.data),
};

// --- Notifications API ---

export interface AgentNotification {
  notification_id: string;
  user_id: string;
  organization_id: string;
  title: string;
  body: string;
  channel: 'in_app' | 'push' | 'email' | 'sms' | 'chat';
  urgency: 'critical' | 'high' | 'normal' | 'low';
  status: 'pending' | 'delivered' | 'read' | 'dismissed' | 'expired';
  source_agent: string;
  source_event_id?: string;
  source_memory_id?: string;
  action_url?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  delivered_at?: string;
  read_at?: string;
}

export const notificationsApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<{ notifications: AgentNotification[]; total: number; unreadCount: number }>>('/notifications', { params }).then(r => r.data),
  getUnreadCount: () =>
    agentClient.get<ApiRes<{ count: number }>>('/notifications/count').then(r => r.data),
  markRead: (id: string) =>
    agentClient.patch<ApiRes<void>>(`/notifications/${id}/read`).then(r => r.data),
  markDismissed: (id: string) =>
    agentClient.patch<ApiRes<void>>(`/notifications/${id}/dismiss`).then(r => r.data),
  markAllRead: () =>
    agentClient.post<ApiRes<void>>('/notifications/mark-all-read').then(r => r.data),
};

// --- User Agent API ---

export interface AgentIdentity {
  name: string;
  aliases: string[];
  personality: string;
  enabled: boolean;
  named_by: 'user' | 'ai';
}

export interface ExpertiseCard {
  user_id: string;
  organization_id: string;
  display_name: string;
  authority_level: 'owner' | 'manager' | 'member';
  preferred_language: string;
  domains: Array<{ name: string; confidence: number }>;
  availability: {
    windows: Array<{ days: string[]; start: string; end: string; timezone: string }>;
    urgentOK: boolean;
  };
  routing: { accept: string[]; decline: string[] };
  response: { avgTimeMinutes: number; preferredChannel: 'chat' | 'email' | 'push' };
  delegate_to?: Array<{ userId: string; topics: string[] }>;
  reports_to?: string;
  agent_identity?: AgentIdentity;
}

export interface UserAgentStatus {
  initialized: boolean;
  onboardingComplete: boolean;
  activeAgents: number;
  running: boolean;
  agentName?: string;
}

export interface LearningControls {
  observeTiming: boolean;
  trackTopics: boolean;
  learnFromCorrections: boolean;
  askProactiveQuestions: boolean;
  crossAgentSignals: boolean;
  usePersonality: boolean;
}

export interface OrgHierarchyMember {
  userId: string;
  displayName: string;
  authorityLevel: 'owner' | 'manager' | 'member';
  preferredLanguage: string;
  domains: Array<{ name: string; confidence: number }>;
  reportsTo?: string;
}

export interface ToolCatalogEntry {
  name: string;
  description: string;
  category: string;
  tier: string;
  capability_tags: string[];
  side_effects: string[];
  idempotent: boolean;
  input_params: Array<{ name: string; type: string; required: boolean; enum?: string[] }>;
}

export interface UserOrchestratorConfig {
  _id: string;
  name: string;
  system_prompt: string;
  resource_preferences: {
    default_model_tier: number;
    max_expert_agents: number;
    budget_priority: 'low' | 'medium' | 'high' | 'critical';
  };
  knowledge_entries: string[];
  voice_config: { blend: Array<{ voice_name: string; weight: number }> } | null;
  capabilities: string[];
  status: string;
}

export const userAgentApi = {
  getStatus: () =>
    agentClient.get<ApiRes<UserAgentStatus>>('/user-agent/status').then(r => r.data),
  getExpertiseCard: () =>
    agentClient.get<ApiRes<ExpertiseCard | null>>('/user-agent/expertise-card').then(r => r.data),
  updateExpertiseCard: (data: Partial<ExpertiseCard>) =>
    agentClient.put<ApiRes<ExpertiseCard>>('/user-agent/expertise-card', data).then(r => r.data),
  submitOnboardingAnswer: (question: string, answer: string) =>
    agentClient.post<ApiRes<{ processed: boolean; nextQuestion: string | null; onboardingComplete: boolean }>>('/user-agent/onboarding-answer', { question, answer }).then(r => r.data),
  setAuthority: (userId: string, level: 'owner' | 'manager' | 'member') =>
    agentClient.put<ApiRes<{ userId: string; level: string }>>(`/user-agent/authority/${userId}`, { level }).then(r => r.data),
  getOrgHierarchy: () =>
    agentClient.get<ApiRes<OrgHierarchyMember[]>>('/user-agent/org-hierarchy').then(r => r.data),
  getLearningControls: () =>
    agentClient.get<ApiRes<{ controls: LearningControls }>>('/user-agent/learning-controls').then(r => r.data),
  setLearningControls: (controls: Partial<LearningControls>) =>
    agentClient.put<ApiRes<{ updated: boolean }>>('/user-agent/learning-controls', { controls }).then(r => r.data),
  getActivityLog: (limit?: number) =>
    agentClient.get<ApiRes<{ activities: Array<{ id: string; type: string; text: string; aspects: string[]; weight: number; createdAt: string }> }>>('/user-agent/activity-log', { params: limit ? { limit: String(limit) } : {} }).then(r => r.data),
  chat: (message: string, chatHistory?: Array<{ role: string; content: string }>, thread_id?: string) =>
    agentClient.post<ApiRes<ChatResponse>>('/user-agent/chat', { message, chatHistory, thread_id }).then(r => r.data),
  pollTaskResult: (taskId: string) =>
    agentClient.get<ApiRes<UserAgentTaskResult>>(`/user-agent/task/${taskId}`).then(r => r.data),
  getAgentIdentity: () =>
    agentClient.get<ApiRes<AgentIdentity>>('/user-agent/agent-identity').then(r => r.data),
  checkAgentName: (name: string) =>
    agentClient.get<ApiRes<{ duplicates: Array<{ userId: string; userDisplayName: string; agentName: string }> }>>('/user-agent/check-agent-name', { params: { name } }).then(r => r.data),
  /** Fire-and-forget memory notification after visual editing */
  notifyActivity: (data: { activityType: string; summary: string; metadata?: Record<string, unknown> }) =>
    agentClient.post<ApiRes<{ success: boolean }>>('/user-agent/notify-activity', data).then(r => r.data),
  getToolsCatalog: () =>
    agentClient.get<ApiRes<{ tools: ToolCatalogEntry[]; count: number }>>('/user-agent/tools-catalog').then(r => r.data),
  getOrchestratorConfig: () =>
    agentClient.get<ApiRes<UserOrchestratorConfig | null>>('/user-agent/orchestrator-config').then(r => r.data),
  updateOrchestratorConfig: (data: Partial<Pick<UserOrchestratorConfig, 'system_prompt' | 'resource_preferences' | 'knowledge_entries' | 'voice_config'>>) =>
    agentClient.put<ApiRes<UserOrchestratorConfig>>('/user-agent/orchestrator-config', data).then(r => r.data),
};

// --- Skills API ---

export interface Skill {
  _id: string;
  skill_id: string;
  name: string;
  description: string;
  current_version: string;
  versions: Array<{
    version: string;
    instructions: string;
    scripts?: string[];
    changelog?: string;
    created_at: string;
    created_by: string;
  }>;
  organization_id: string;
  domain?: string;
  tags: string[];
  capabilities: string[];
  triggers: Array<{ type: string; config: Record<string, unknown> }>;
  status: 'draft' | 'active' | 'canary' | 'deprecated';
  origin: 'human' | 'agent' | 'system';
  use_count: number;
  success_rate: number;
  last_used_at?: string;
  canary_percentage?: number;
  source_workflow_id?: string;
  source_conversation_id?: string;
  created_by: string;
  createdAt: string;
  updatedAt?: string;
}

export const skillsApi = {
  list: (params?: Record<string, string>) =>
    agentClient.get<ApiRes<{ skills: Skill[]; total: number }>>('/skills', { params }).then(r => r.data),
  get: (id: string) =>
    agentClient.get<ApiRes<Skill>>(`/skills/${id}`).then(r => r.data),
  create: (data: { name: string; description: string; instructions: string; domain?: string; tags?: string[] }) =>
    agentClient.post<ApiRes<Skill>>('/skills', data).then(r => r.data),
  update: (id: string, data: Partial<Pick<Skill, 'name' | 'description' | 'domain' | 'tags' | 'capabilities' | 'triggers' | 'status' | 'canary_percentage'>>) =>
    agentClient.put<ApiRes<Skill>>(`/skills/${id}`, data).then(r => r.data),
  activate: (id: string) =>
    agentClient.post<ApiRes<Skill>>(`/skills/${id}/activate`).then(r => r.data),
  deprecate: (id: string) =>
    agentClient.post<ApiRes<Skill>>(`/skills/${id}/deprecate`).then(r => r.data),
  addVersion: (id: string, data: { instructions: string; version: string; changelog?: string; scripts?: string[] }) =>
    agentClient.post<ApiRes<Skill>>(`/skills/${id}/versions`, data).then(r => r.data),
  feedback: (id: string, success: boolean) =>
    agentClient.post<ApiRes<void>>(`/skills/${id}/feedback`, { success }).then(r => r.data),
  search: (data: { query: string; domain?: string; limit?: number }) =>
    agentClient.post<ApiRes<Array<{ skill_id: string; name: string; description: string; score: number }>>>('/skills/search', data).then(r => r.data),
};

// --- Missions API ---

import type { Mission, MissionLog, StandupReport } from '@/types/mission';

export const missionsApi = {
  list: (statusFilter?: string) =>
    agentClient.get<ApiRes<Mission[]>>(`/missions${statusFilter ? `?status=${statusFilter}` : ''}`).then(r => r.data),
  get: (id: string) =>
    agentClient.get<ApiRes<{ mission: Mission }>>(`/missions/${id}`).then(r => r.data),
  create: (data: { title: string; objective: string; costAllocatedUsd?: number; tokensAllocated?: number; dailyTokenLimit?: number }) =>
    agentClient.post<ApiRes<{ mission_id: string }>>('/missions', data).then(r => r.data),
  approve: (id: string) =>
    agentClient.post<ApiRes<Mission>>(`/missions/${id}/approve`).then(r => r.data),
  start: (id: string) =>
    agentClient.post<ApiRes<Mission>>(`/missions/${id}/start`).then(r => r.data),
  pause: (id: string, reason?: string) =>
    agentClient.post<ApiRes<Mission>>(`/missions/${id}/pause`, { reason }).then(r => r.data),
  resume: (id: string) =>
    agentClient.post<ApiRes<Mission>>(`/missions/${id}/resume`).then(r => r.data),
  cancel: (id: string) =>
    agentClient.post<ApiRes<Mission>>(`/missions/${id}/cancel`).then(r => r.data),
  approvePlan: (id: string) =>
    agentClient.post<ApiRes<Mission>>(`/missions/${id}/plan/approve`).then(r => r.data),
  instruct: (id: string, text: string) =>
    agentClient.post<ApiRes<void>>(`/missions/${id}/instruct`, { text }).then(r => r.data),
  feedback: (id: string, rating: number, feedback: string) =>
    agentClient.post<ApiRes<void>>(`/missions/${id}/feedback`, { rating, feedback }).then(r => r.data),
  getLogs: (id: string, limit = 50) =>
    agentClient.get<ApiRes<MissionLog[]>>(`/missions/${id}/logs?limit=${limit}`).then(r => r.data),
  getStandup: (id: string) =>
    agentClient.get<ApiRes<StandupReport>>(`/missions/${id}/standup`).then(r => r.data),
  overridePhase: (id: string, phase: string) =>
    agentClient.patch<ApiRes<Mission>>(`/missions/${id}/phase`, { phase }).then(r => r.data),
  toggleStepConfirm: (id: string, enabled: boolean) =>
    agentClient.patch<ApiRes<void>>(`/missions/${id}/step-confirmation`, { enabled }).then(r => r.data),
};

// --- Projects API ---

export const projectsApi = {
  list: (includeStats?: boolean) =>
    agentClient.get('/projects', { params: includeStats ? { include: 'stats' } : {} }).then(r => r.data),
  get: (id: string) =>
    agentClient.get(`/projects/${id}`).then(r => r.data),
  getMyTasks: (status?: string) =>
    agentClient.get('/tasks/mine', { params: status ? { status } : {} }).then(r => r.data),
  updateStatus: (id: string, status: string) =>
    agentClient.patch(`/projects/${id}`, { status }).then(r => r.data),
};

// --- Health API ---

export const healthApi = {
  check: () =>
    axios.get<{ status: string; service: string }>(`${appConfig.api.agentServiceUrl}/health`).then(r => r.data),
};

// --- Style Intelligence API ---

export const styleIntelligenceApi = {
  analyzeScreenshot: (data: { image: string; mimeType: string; name?: string }) =>
    agentClient.post<ApiRes<IStyleProfile>>('/style-intelligence/analyze-screenshot', data).then(r => r.data),

  analyzeUrl: (data: { url: string; name?: string }) =>
    agentClient.post<ApiRes<IStyleProfile>>('/style-intelligence/analyze-url', data).then(r => r.data),

  getProfiles: (params?: { projectId?: string }) =>
    agentClient.get<ApiRes<IStyleProfile[]>>('/style-intelligence/profiles', { params }).then(r => r.data),

  getProfile: (id: string) =>
    agentClient.get<ApiRes<IStyleProfile>>(`/style-intelligence/profiles/${id}`).then(r => r.data),

  updateProfile: (id: string, data: Partial<IStyleProfile>) =>
    agentClient.put<ApiRes<IStyleProfile>>(`/style-intelligence/profiles/${id}`, data).then(r => r.data),

  activateProfile: (id: string, projectId?: string) =>
    agentClient.post<ApiRes<{ directiveMemoryIds: string[]; warnings?: string[] }>>(`/style-intelligence/profiles/${id}/activate`, { projectId }).then(r => r.data),

  deactivateProfile: (id: string, projectId?: string) =>
    agentClient.post<ApiRes<{ success: true }>>(`/style-intelligence/profiles/${id}/deactivate`, { projectId }).then(r => r.data),

  deleteProfile: (id: string) =>
    agentClient.delete<ApiRes<{ success: true }>>(`/style-intelligence/profiles/${id}`).then(r => r.data),

  // Sharing & Discovery
  browseSharedProfiles: (params?: { search?: string; tags?: string; sort?: string; page?: number; limit?: number }) =>
    agentClient.get<ApiRes<StyleProfileBrowseCard[]> & { total: number }>('/style-intelligence/profiles/browse', { params }).then(r => r.data),

  getSharedProfile: (id: string) =>
    agentClient.get<ApiRes<IStyleProfile>>(`/style-intelligence/profiles/browse/${id}`).then(r => r.data),

  importProfile: (id: string) =>
    agentClient.post<ApiRes<IStyleProfile>>(`/style-intelligence/profiles/${id}/import`, {}).then(r => r.data),
};

// --- Diagnostic Traces ---

export interface DiagnosticTraceEntry {
  step: string;
  status: 'ok' | 'error' | 'warning' | 'timeout';
  timestamp: string;
  elapsed_ms: number;
  data: Record<string, unknown>;
}

export interface DiagnosticTraceSummary {
  queryTraceId: string;
  userId: string;
  organizationId: string;
  userMessage: string;
  entries: DiagnosticTraceEntry[];
  started_at: string;
  completed_at?: string;
  total_duration_ms?: number;
  created_at: string;
}

export interface TracedUser {
  orgId: string;
  userId: string;
  ttl: number;
}

export const diagnosticsApi = {
  enable: (data: { userId: string; orgId: string; ttlHours: number }) =>
    agentClient.post<{ success: boolean }>('/diagnostics/enable', data).then((r) => r.data),
  disable: (data: { userId: string; orgId: string }) =>
    agentClient.delete<{ success: boolean }>('/diagnostics/disable', { data }).then((r) => r.data),
  status: () =>
    agentClient.get<ApiRes<{ tracedUsers: TracedUser[] }>>('/diagnostics/status').then((r) => r.data),
  listTraces: (params?: Record<string, string | number>) =>
    agentClient.get<ApiRes<{ traces: DiagnosticTraceSummary[] }>>('/diagnostics/traces', { params }).then((r) => r.data),
  getTrace: (traceId: string) =>
    agentClient.get<ApiRes<DiagnosticTraceSummary>>(`/diagnostics/traces/${traceId}`).then((r) => r.data),
};

// ── Scheduled Activations API ────────────────────────────────────────────────
export type ActivationStatus = 'pending' | 'active' | 'completed' | 'cancelled' | 'paused' | 'failed';
export type ActivationTypeValue = 'one_shot' | 'recurring';
export type ExecutionStatusValue = 'success' | 'failure' | 'deferred';

export interface ActiveHours {
  start: string;
  end: string;
  timezone: string;
  days?: string[];
}

export interface ScheduledActivation {
  activation_id: string;
  agent_id: string;
  agent_role: string;
  organization_id: string;
  activation_type: ActivationTypeValue;
  status: ActivationStatus;
  next_fire_at: string;
  interval_ms?: number;
  cron_expression?: string;
  active_hours?: ActiveHours;
  reason: string;
  context: Record<string, unknown>;
  created_at: string;
  last_fired_at?: string;
  fire_count: number;
  failure_count?: number;
  max_fires?: number;
  expires_at?: string;
}

export interface ActivationExecution {
  execution_id: string;
  activation_id: string;
  action?: string;
  status: ExecutionStatusValue;
  fired_at: string;
  duration_ms?: number;
  attempt_number: number;
  fire_count_after?: number;
  result_summary?: string;
  error_message?: string;
  error_code?: string;
  error_stack?: string;
}

export const activationsApi = {
  list: (status?: ActivationStatus[]): Promise<ApiRes<ScheduledActivation[]>> => {
    const params = status?.length ? { status: status.join(',') } : undefined;
    return agentClient
      .get<ApiRes<ScheduledActivation[]>>('/activations', { params })
      .then(r => r.data);
  },

  get: (id: string): Promise<ApiRes<ScheduledActivation>> =>
    agentClient.get<ApiRes<ScheduledActivation>>(`/activations/${id}`).then(r => r.data),

  cancel: (id: string): Promise<ApiRes<null>> =>
    agentClient.delete<ApiRes<null>>(`/activations/${id}`).then(r => r.data),

  listExecutions: (id: string, limit = 50): Promise<ApiRes<ActivationExecution[]>> =>
    agentClient
      .get<ApiRes<ActivationExecution[]>>(`/activations/${id}/executions`, { params: { limit } })
      .then(r => r.data),

  recentFailures: (sinceHours = 24): Promise<ApiRes<ActivationExecution[]>> =>
    agentClient
      .get<ApiRes<ActivationExecution[]>>('/activations/recent-failures', { params: { sinceHours } })
      .then(r => r.data),
};

// ── Agent Voice API ──────────────────────────────────────────────────────────
export const KOKORO_VOICES = [
  'af_heart', 'af_bella', 'af_nicole', 'af_aoede', 'af_kore', 'af_sarah',
  'af_alloy', 'af_nova', 'af_sky', 'af_jessica', 'af_river',
  'am_michael', 'am_fenrir', 'am_puck', 'am_adam', 'am_echo', 'am_eric',
  'am_liam', 'am_onyx', 'am_santa',
  'bf_alice', 'bf_emma', 'bf_isabella', 'bf_lily',
  'bm_daniel', 'bm_fable', 'bm_george', 'bm_lewis',
] as const;

export type KokoroVoiceName = (typeof KOKORO_VOICES)[number];

export interface VoiceBlendEntry { voice_name: string; weight: number; }

export const agentVoiceApi = {
  list: (): Promise<ApiRes<OrchestratorConfig[]>> =>
    agentClient.get<ApiRes<OrchestratorConfig[]>>('/agent-voice/').then((r) => r.data),

  personalAgent: (): Promise<ApiRes<OrchestratorConfig | null>> =>
    agentClient.get<ApiRes<OrchestratorConfig | null>>('/agent-voice/personal-agent').then((r) => r.data),

  update: (id: string, blend: VoiceBlendEntry[]): Promise<ApiRes<OrchestratorConfig>> =>
    agentClient.put<ApiRes<OrchestratorConfig>>(`/agent-voice/${id}`, { blend }).then((r) => r.data),
};


/**
 * Normalize display text for natural TTS speech.
 * Converts numbers, abbreviations, and symbols to spoken form via an LLM pass.
 * Always returns a string — falls back to the original text on any error.
 * Uses agentClient (port 8003), NOT apiClient (port 8000).
 */
export async function normalizeForSpeech(text: string): Promise<string> {
  if (!text || !text.trim()) return text;
  try {
    const response = await agentClient.post<unknown>(
      '/voice-completions/normalize',
      { text },
      { timeout: 16000 },  // Override 120s default; allows up to ~15s for LLM normalize call
    );
    const speech = isRecord(response.data) ? response.data.speech : undefined;
    return typeof speech === 'string' && speech.trim() ? speech : text;
  } catch {
    // Silently fall back — TTS must always fire regardless of normalize outcome
    return text;
  }
}

// ── HA automation edit (conversational, config-native; no ProposedWorkflow) ──

export const haAgentApi = {
  async editAutomation(params: {
    instanceId: string;
    automationId: string;
    currentConfig: HAAutomationConfig;
    instruction: string;
  }): Promise<{ modifiedConfig: HAAutomationConfig; reasoning: string }> {
    const { data } = await agentClient.post<unknown>(
      '/integrations/homeassistant/automations/edit',
      params,
    );
    const modifiedConfig = isHAAutomationConfigResponse(data)
      ? data.modifiedConfig
      : undefined;

    if (!modifiedConfig) {
      throw new Error('Invalid Home Assistant automation edit response');
    }

    return {
      modifiedConfig,
      reasoning: isRecord(data) && typeof data.reasoning === 'string' ? data.reasoning : '',
    };
  },
};

function isHAAutomationConfigResponse(
  value: unknown,
): value is { modifiedConfig: HAAutomationConfig; reasoning?: unknown } {
  return isRecord(value) && isHAAutomationConfig(value.modifiedConfig);
}

function isHAAutomationConfig(value: unknown): value is HAAutomationConfig {
  if (!isRecord(value)) return false;
  if (typeof value.alias !== 'string') return false;
  if (!Array.isArray(value.trigger) || !value.trigger.every(isHATriggerConfig)) return false;
  if (!Array.isArray(value.action) || !value.action.every(isRecord)) return false;
  if (value.condition !== undefined && (!Array.isArray(value.condition) || !value.condition.every(isHAConditionConfig))) {
    return false;
  }
  if (value.mode !== undefined && !['single', 'restart', 'queued', 'parallel'].includes(String(value.mode))) {
    return false;
  }
  if (value.max !== undefined && typeof value.max !== 'number') return false;
  if (
    value.max_exceeded !== undefined &&
    !['silent', 'warning', 'error'].includes(String(value.max_exceeded))
  ) {
    return false;
  }
  if (value.variables !== undefined && !isRecord(value.variables)) return false;
  return true;
}

function isHATriggerConfig(value: unknown): boolean {
  return isRecord(value) && typeof value.platform === 'string';
}

function isHAConditionConfig(value: unknown): boolean {
  return isRecord(value) && typeof value.condition === 'string';
}
